import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { APP_VERSION, getVersionLabel } from "@/versionInfo";
import { usePackagesState } from "@/store";
import { UploadClientStatus } from "@/store/packagesSlice";

const VERSION_STORAGE_KEY = "clipn:currentAppVersion";
const LAST_RELOAD_KEY = "clipn:lastReloadedVersion";
const LAST_REMOTE_KEY = "clipn:lastSeenRemoteVersion";
const LAST_CHECKED_AT_KEY = "clipn:lastVersionCheckAt";
const PENDING_VERSION_KEY = "clipn:pendingVersion";
const CHECK_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const DEFAULT_FORCED_REFRESH_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const FORCED_REFRESH_TIMEOUT_MS = Number.parseInt(
  process.env.REACT_APP_FORCED_REFRESH_TIMEOUT_MS || "",
  10
);
const EFFECTIVE_FORCED_REFRESH_TIMEOUT_MS =
  Number.isFinite(FORCED_REFRESH_TIMEOUT_MS) && FORCED_REFRESH_TIMEOUT_MS > 0
    ? FORCED_REFRESH_TIMEOUT_MS
    : DEFAULT_FORCED_REFRESH_TIMEOUT_MS;

const fetchLatestVersion = async (signal) => {
  const response = await fetch(`/version.json?ts=${Date.now()}`, {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Version check failed with status ${response.status}`);
  }

  return response.json();
};

const isUploadActive = (upload) => {
  if (!upload) {
    return false;
  }
  const { status } = upload;
  return (
    status === UploadClientStatus.UPLOADING ||
    status === UploadClientStatus.VERIFYING
  );
};

export const VersionUpdateManager = () => {
  const isBrowser = typeof window !== "undefined";
  const isProd = process.env.NODE_ENV === "production";
  const shouldMonitor = process.env.NODE_ENV !== "development";
  const shouldAutoReload = isProd;
  const { uploadingPackages = [] } = usePackagesState();
  const [pendingVersion, setPendingVersion] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [shouldReload, setShouldReload] = useState(false);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const forcedRefreshTimeoutRef = useRef(null);
  const forcedRefreshVersionRef = useRef(null);
  const [showForcedRefreshPrompt, setShowForcedRefreshPrompt] = useState(false);
  const [forcedRefreshDueAt, setForcedRefreshDueAt] = useState(null);

  const activeUploadCount = useMemo(
    () => uploadingPackages.filter((pkg) => isUploadActive(pkg)).length,
    [uploadingPackages]
  );

  const hasActiveUploads = activeUploadCount > 0;

  const syncStoredVersion = useCallback(() => {
    if (!isBrowser || !shouldMonitor) {
      return;
    }
    try {
      const storedVersion = window.localStorage.getItem(VERSION_STORAGE_KEY);
      if (storedVersion !== APP_VERSION.version) {
        window.localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION.version);
      }
      const lastReloaded = window.localStorage.getItem(LAST_RELOAD_KEY);
      if (lastReloaded && lastReloaded === APP_VERSION.version) {
        window.localStorage.removeItem(LAST_RELOAD_KEY);
      }
    } catch (error) {
      console.warn("Unable to sync version storage", error);
    }
  }, [isBrowser, shouldMonitor]);

  useEffect(() => {
    if (!shouldMonitor) {
      return;
    }

    mountedRef.current = true;
    syncStoredVersion();

    if (isBrowser) {
      console.info(
        `[ClipN] Running frontend ${getVersionLabel()} built at ${APP_VERSION.buildTime}`
      );
    }

    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isBrowser, shouldMonitor, syncStoredVersion]);

  const requestLatestVersion = useCallback(async () => {
    if (!isBrowser || !shouldMonitor) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsChecking(true);
    setCheckError(null);

    try {
      const latest = await fetchLatestVersion(controller.signal);
      if (!mountedRef.current) {
        return;
      }

      setLastCheckedAt(new Date().toISOString());
      try {
        window.localStorage.setItem(LAST_CHECKED_AT_KEY, new Date().toISOString());
      } catch (error) {
        console.warn("Unable to persist last checked time", error);
      }

      try {
        const stored = window.localStorage.getItem(VERSION_STORAGE_KEY);
        if (!stored) {
          window.localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION.version);
        }
      } catch (error) {
        console.warn("Unable to read stored version", error);
      }

      if (latest?.version) {
        try {
          window.localStorage.setItem(LAST_REMOTE_KEY, latest.version);
        } catch (error) {
          console.warn("Unable to persist last remote version", error);
        }
      }

      const isDifferentFromCurrent =
        latest?.version && latest.version !== APP_VERSION.version;

      if (isDifferentFromCurrent) {
        setPendingVersion(latest);
        setShouldReload(true);
        try {
          window.localStorage.setItem(PENDING_VERSION_KEY, JSON.stringify(latest));
        } catch (error) {
          console.warn("Unable to persist pending version", error);
        }
      } else {
        setPendingVersion(null);
        setShouldReload(false);
        try {
          window.localStorage.removeItem(PENDING_VERSION_KEY);
        } catch (error) {
          console.warn("Unable to clear pending version", error);
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      if (!mountedRef.current) {
        return;
      }
      console.error("Version check failed", error);
      setCheckError(error.message);
    } finally {
      if (mountedRef.current) {
        setIsChecking(false);
      }
    }
  }, [isBrowser, shouldMonitor]);

  useEffect(() => {
    if (!shouldMonitor) {
      return;
    }

    requestLatestVersion();

    if (!isBrowser) {
      return;
    }

    const intervalId = window.setInterval(
      () => requestLatestVersion(),
      CHECK_INTERVAL_MS
    );
    const handleFocus = () => requestLatestVersion();

    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isBrowser, requestLatestVersion, shouldMonitor]);

  const reloadNow = useCallback(
    (force = false) => {
      if (!isBrowser || !pendingVersion?.version || !shouldMonitor) {
        return;
      }

      try {
        const lastReloaded = window.localStorage.getItem(LAST_RELOAD_KEY);
        if (!force && lastReloaded === pendingVersion.version) {
          return;
        }
        window.localStorage.setItem(LAST_RELOAD_KEY, pendingVersion.version);
      } catch (error) {
        console.warn("Unable to persist reload guard", error);
      }

      window.location.reload();
    },
    [isBrowser, pendingVersion, shouldMonitor]
  );

  useEffect(() => {
    if (!shouldMonitor || !pendingVersion || !shouldReload || hasActiveUploads) {
      return;
    }
    if (!shouldAutoReload) {
      return;
    }
    reloadNow(false);
  }, [
    pendingVersion,
    shouldReload,
    hasActiveUploads,
    reloadNow,
    shouldAutoReload,
    shouldMonitor,
  ]);

  useEffect(() => {
    const shouldDelayForUploads =
      shouldMonitor && pendingVersion?.version && shouldReload && hasActiveUploads;

    if (!shouldDelayForUploads) {
      if (forcedRefreshTimeoutRef.current) {
        window.clearTimeout(forcedRefreshTimeoutRef.current);
        forcedRefreshTimeoutRef.current = null;
      }
      forcedRefreshVersionRef.current = null;
      setShowForcedRefreshPrompt(false);
      setForcedRefreshDueAt(null);
      return;
    }

    if (forcedRefreshVersionRef.current === pendingVersion.version) {
      return;
    }

    forcedRefreshVersionRef.current = pendingVersion.version;
    setShowForcedRefreshPrompt(false);
    const dueAt = new Date(Date.now() + EFFECTIVE_FORCED_REFRESH_TIMEOUT_MS).toISOString();
    setForcedRefreshDueAt(dueAt);

    forcedRefreshTimeoutRef.current = window.setTimeout(() => {
      if (!mountedRef.current) {
        return;
      }
      setShowForcedRefreshPrompt(true);
    }, EFFECTIVE_FORCED_REFRESH_TIMEOUT_MS);

    return () => {
      if (forcedRefreshTimeoutRef.current) {
        window.clearTimeout(forcedRefreshTimeoutRef.current);
        forcedRefreshTimeoutRef.current = null;
      }
    };
  }, [hasActiveUploads, pendingVersion, shouldMonitor, shouldReload]);

  if (!shouldMonitor || !pendingVersion) {
    return null;
  }

  const latestLabel = getVersionLabel(pendingVersion);
  const currentLabel = getVersionLabel();
  const buildTimeLabel = pendingVersion.buildTime
    ? new Date(pendingVersion.buildTime).toLocaleString()
    : "";
  const forcedRefreshDueLabel = forcedRefreshDueAt
    ? new Date(forcedRefreshDueAt).toLocaleTimeString()
    : null;

  return (
    <>
      <Banner
        role="status"
        aria-live="polite"
        data-testid="version-update-banner"
      >
        <BannerContent>
          <BannerTitle>New version available</BannerTitle>
          <BannerCopy>
            You are running <strong>{currentLabel}</strong>.{" "}
            <strong>{latestLabel}</strong> was deployed at {buildTimeLabel}.
          </BannerCopy>
          {hasActiveUploads ? (
            <BannerCopy>
              Finishing {activeUploadCount} upload
              {activeUploadCount === 1 ? "" : "s"} before refreshing automatically.
            </BannerCopy>
          ) : (
            <BannerCopy>
              {shouldAutoReload
                ? "The page will refresh automatically shortly."
                : "Auto-refresh is disabled in this environment. Please refresh manually."}
            </BannerCopy>
          )}
          {checkError && (
            <BannerCopy role="alert">
              Last version check failed: {checkError}. Retrying automatically.
            </BannerCopy>
          )}
          {lastCheckedAt && (
            <LastChecked>
              Last checked {new Date(lastCheckedAt).toLocaleTimeString()}.
            </LastChecked>
          )}
        </BannerContent>
        <BannerActions>
          <RefreshButton
            type="button"
            onClick={() => reloadNow(true)}
            disabled={hasActiveUploads}
          >
            Refresh now
          </RefreshButton>
          <SecondaryButton
            type="button"
            onClick={() => requestLatestVersion()}
            disabled={isChecking}
          >
            {isChecking ? "Checking…" : "Check again"}
          </SecondaryButton>
        </BannerActions>
      </Banner>
      {showForcedRefreshPrompt && hasActiveUploads && (
        <ModalOverlay role="alertdialog" aria-modal="true">
          <ModalCard>
            <ModalTitle>Update requires refresh</ModalTitle>
            <ModalCopy>
              A newer version is ready, but uploads are still active. Refresh is paused to avoid
              interrupting uploads.
            </ModalCopy>
            {forcedRefreshDueLabel && (
              <ModalCopy>
                Refresh has been delayed since {forcedRefreshDueLabel}. You can refresh now if
                needed.
              </ModalCopy>
            )}
            <ModalActions>
              <ModalPrimaryButton type="button" onClick={() => reloadNow(true)}>
                Refresh now
              </ModalPrimaryButton>
              <ModalSecondaryButton
                type="button"
                onClick={() => setShowForcedRefreshPrompt(false)}
              >
                Keep waiting
              </ModalSecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

const Banner = styled.div`
  width: 100%;
  background-color: #1b2432;
  color: #ffffff;
  padding: 12px 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  line-height: 1.4;
`;

const BannerTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const BannerCopy = styled.div`
  font-size: 13px;
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const RefreshButton = styled.button`
  border: none;
  background-color: ${({ disabled }) => (disabled ? "#5f6470" : "#f9b000")};
  color: #1b2432;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #ffffff;
  background-color: transparent;
  color: #ffffff;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const LastChecked = styled.div`
  font-size: 11px;
  opacity: 0.8;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(10, 14, 20, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: min(520px, 100%);
  background: #ffffff;
  color: #1b2432;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
`;

const ModalCopy = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const ModalPrimaryButton = styled.button`
  border: none;
  background: #f9b000;
  color: #1b2432;
  border-radius: 4px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
`;

const ModalSecondaryButton = styled.button`
  border: 1px solid #1b2432;
  background: transparent;
  color: #1b2432;
  border-radius: 4px;
  padding: 8px 14px;
  font-weight: 500;
  cursor: pointer;
`;
